import { Group, Stack, Stepper, useMatches } from "@mantine/core";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { isEmpty, uniq } from "lodash";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "./CreateCarForm.module.css";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import {
  handleGenerationChangeFC,
  handleMakeSelection,
  handleModeChangeFC,
  nextStepHandler,
  selectDefaultVariantFC,
  updateCarFamilyAndResetFields,
  updateProductionYearsByVariant,
  validateStepFields,
} from "./functions/CreateCarForm.functions";

import { newCarSchema } from "@/services/cars/create-car";
import {
  useCarFamilies,
  useCarGenerations,
  useCarModels,
  useCarVariants,
} from "@/services/cars/get-cars-models";
import { useProfile } from "@/services/profile/get-profile";

import type { CreateCarInput } from "@/services/cars/create-car";
import type { List } from "@/types/entity";
import type { Garage } from "@/types/garage";
import type { Dispatch, FC, SetStateAction } from "react";

export type CreateCarFormProps = {
  onSubmit: (values: CreateCarInput) => void;
  onCancel?: () => void;
  initialValues?: Partial<CreateCarInput>;
  garageId?: string | undefined;
  garages: List<Garage> | undefined;
  loading?: boolean;
  setSelectedGarageForCarCreation?: Dispatch<SetStateAction<string | undefined>>;
  showLessElements?: boolean;
};

export const CreateCarForm: FC<CreateCarFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  garageId,
  garages,
  loading = false,
  setSelectedGarageForCarCreation,
  showLessElements = false,
}) => {
  const { t } = useTranslation();
  const [minProductionYear, setMinProductionYear] = useState<number>(1900);
  const [maxProductionYear, setMaxProductionYear] = useState<number>(new Date().getFullYear());
  const [activeStep, setActiveStep] = useState(0);

  const { data: user } = useProfile();

  const firstName = user?.additional_info?.first_name ?? user?.name ?? "";
  const lastName = user?.additional_info?.last_name ?? "";
  const nameParts = [firstName, lastName].filter((p) => p !== undefined && p !== null && p !== "");
  const fullName = nameParts.join(" ");

  const NOVARIANT = "no_variant";

  const form = useForm<CreateCarInput>({
    initialValues: {
      nickname: "",
      owner: fullName || "",
      purchase_price: 0,
      purchase_date: dayjs(new Date()).format("YYYY-MM-DD"),
      production_year: new Date().getFullYear(),
      make: "",
      family: "",
      generation: "",
      model: "",
      variant: NOVARIANT,
      licence_plate: "",
      country_of_registration: "",
      color: "",
      interior_colors: "",
      condition: "",
      number_of_previous_owner: 0,
      status: "owned",
      special_equipment: "",
      vin: "",
      ...initialValues,
    },
    validate: zod4Resolver(newCarSchema),
    validateInputOnBlur: true,
  });

  const { data: carFamilies, isLoading: isLoadingFamilies } = useCarFamilies({
    make: form.values.make,
  });

  const { data: carGenerations, isLoading: isLoadingGenerations } = useCarGenerations({
    make: form.values.make,
    family: form.values.family,
  });

  const { data: carModels, isLoading: isLoadingModels } = useCarModels({
    make: form.values.make,
    family: form.values.family,
    generation: form.values.generation,
  });

  const { data: carVariants, isLoading: isLoadingVariants } = useCarVariants({
    make: form.values.make,
    family: form.values.family,
    generation: form.values.generation,
    model: form.values.model,
  });

  const familyOptions =
    carFamilies?.map((item) => ({
      value: item.family,
      label: item.family,
    })) || [];

  const generationOptions =
    carGenerations?.map((item) => ({
      value: item.generation,
      label: item.generation,
    })) || [];

  const modelOptions =
    uniq(carModels?.map((model) => model.model)).map((item) => ({
      value: item,
      label: item,
    })) || [];

  const variantOptions = carVariants
    ? carVariants.map((item) => ({
        value: item.variant || NOVARIANT,
        label: item.variant || t("car.no_variant"),
      }))
    : [];

  const handleMakeChange = handleMakeSelection(form, NOVARIANT);
  const handleFamilyChange = updateCarFamilyAndResetFields(form, NOVARIANT);
  const handleGenerationChange = handleGenerationChangeFC(form, NOVARIANT);

  const handleModelChange = handleModeChangeFC(
    form,
    NOVARIANT,
    setMinProductionYear,
    setMaxProductionYear
  );

  const setMinMaxProdYearsByVariant = updateProductionYearsByVariant(
    carVariants,
    NOVARIANT,
    setMinProductionYear,
    setMaxProductionYear,
    form
  );

  const selectDefaultVariant = selectDefaultVariantFC(NOVARIANT, form, setMinMaxProdYearsByVariant);

  const handleVariantChange = (value?: string | null) => {
    form.setFieldValue("variant", value || NOVARIANT);
    setMinMaxProdYearsByVariant(value || NOVARIANT);
  };

  useEffect(() => {
    if (carVariants) selectDefaultVariant(carVariants);
  }, [carVariants]);

  const handleSubmit = (values: CreateCarInput) => {
    onSubmit({
      ...values,
      purchase_date: dayjs(values.purchase_date).format("YYYY-MM-DD"),
      variant: values.variant === NOVARIANT ? "" : values.variant,
      condition: values.condition ? values.condition : "",
    });
  };

  const isMobile = useMatches({ base: true, md: false });
  const WrapperForFields = isMobile ? Stack : Group;
  const wrapperProps = isMobile ? { gap: "md" } : { grow: true, align: "flex-start" };

  const isEdit = !isEmpty(initialValues);

  const stepFields: Record<number, (keyof CreateCarInput)[]> = {
    0: ["owner", "purchase_price", "purchase_date", "make", "production_year"],
    1: ["family", "generation", "model", "variant", "status"],
    2: [],
    3: [],
  };

  const isStepValid = validateStepFields(stepFields, form);
  const nextStep = nextStepHandler(stepFields, activeStep, form, setActiveStep);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stepper
        active={activeStep}
        classNames={{
          steps: classes.steps,
          step: classes.step,
        }}
        onStepClick={(step) => {
          if (step <= activeStep) {
            setActiveStep(step);
          }
        }}
      >
        <Stepper.Step label={t("components.CreateCarForm.basics")} py="lg">
          <Step1
            garages={garages}
            setSelectedGarageForCarCreation={setSelectedGarageForCarCreation}
            garageId={garageId}
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            isEdit={isEdit}
            showLessElements={showLessElements}
            handleMakeChange={handleMakeChange}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            nextStep={nextStep}
            isStepValid={isStepValid}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.carDetails")} py="lg">
          <Step2
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            showLessElements={showLessElements}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            minProductionYear={minProductionYear}
            maxProductionYear={maxProductionYear}
            nextStep={nextStep}
            isStepValid={isStepValid}
            familyOptions={familyOptions}
            isLoadingFamilies={isLoadingFamilies}
            handleFamilyChange={handleFamilyChange}
            isLoadingGenerations={isLoadingGenerations}
            generationOptions={generationOptions}
            handleGenerationChange={handleGenerationChange}
            isLoadingModels={isLoadingModels}
            modelOptions={modelOptions}
            handleModelChange={handleModelChange}
            variantOptions={variantOptions}
            isLoadingVariants={isLoadingVariants}
            handleVariantChange={handleVariantChange}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.extraDetails")} py="lg">
          <Step3
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            showLessElements={showLessElements}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            nextStep={nextStep}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.review")} py="lg">
          <Step4
            form={form}
            loading={loading}
            onCancel={onCancel}
            onSubmit={form.onSubmit(handleSubmit)}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        </Stepper.Step>
      </Stepper>
    </form>
  );
};
